import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useRepositoryStore } from '../stores/useRepositoryStore';
import { useSSE } from '../hooks/useSSE';
import { messages } from '../constants/messages';
import { Repository, FrozenBranch } from '../types';
import { PageHeader, SearchInput, LoadingSpinner, Button, Badge } from '../components/ui';
import ExemptionModal from '../components/ExemptionModal';
import BranchSelector from '../components/BranchSelector';

function formatLastAction(lastAction: string | Date | undefined): string {
  if (!lastAction) return 'Never';

  const date = new Date(lastAction);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Today';
  if (days === 1) return '1d ago';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function getBranchLastAction(branchData: FrozenBranch | undefined): string | Date | undefined {
  if (!branchData) return undefined;

  // Get the most recent action (frozenAt or unfrozenAt)
  const frozenAt = branchData.frozenAt ? new Date(branchData.frozenAt) : null;
  const unfrozenAt = branchData.unfrozenAt ? new Date(branchData.unfrozenAt) : null;

  if (!frozenAt && !unfrozenAt) return undefined;
  if (!frozenAt) return unfrozenAt!;
  if (!unfrozenAt) return frozenAt;

  // Return the most recent timestamp
  return frozenAt > unfrozenAt ? frozenAt : unfrozenAt;
}

function formatActivityTime(timestamp: Date): string {
  const now = new Date();
  const diff = now.getTime() - timestamp.getTime();
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function RepositoriesPage() {
  const { authStatus } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Use Zustand store
  const repositories = useRepositoryStore(state => state.repositories);
  const organizationInfo = useRepositoryStore(state => state.organizationInfo);
  const activityLog = useRepositoryStore(state => state.activityLog);
  const activityLoading = useRepositoryStore(state => state.activityLoading);
  const hasMoreActivity = useRepositoryStore(state => state.hasMoreActivity);
  const loading = useRepositoryStore(state => state.loading);
  const initialLoad = useRepositoryStore(state => state.initialLoad);
  const setCurrentInstallation = useRepositoryStore(state => state.setCurrentInstallation);
  const loadRepositories = useRepositoryStore(state => state.loadRepositories);
  const loadActivityHistory = useRepositoryStore(state => state.loadActivityHistory);
  const loadMoreActivity = useRepositoryStore(state => state.loadMoreActivity);
  const freezeRepository = useRepositoryStore(state => state.freezeRepository);
  const unfreezeRepository = useRepositoryStore(state => state.unfreezeRepository);
  const freezeBranch = useRepositoryStore(state => state.freezeBranch);
  const unfreezeBranch = useRepositoryStore(state => state.unfreezeBranch);
  const bulkFreezeRepositories = useRepositoryStore(state => state.bulkFreezeRepositories);
  const bulkUnfreezeRepositories = useRepositoryStore(state => state.bulkUnfreezeRepositories);
  const updateExemptions = useRepositoryStore(state => state.updateExemptions);
  const [selectedRepos, setSelectedRepos] = useState<Set<string>>(new Set());
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'alphabetical'>('recent');
  const [showGitHubProNotice, setShowGitHubProNotice] = useState(
    () => localStorage.getItem('hideGitHubProNotice') !== 'true'
  );
  const [exemptionModalOpen, setExemptionModalOpen] = useState(false);
  const [selectedRepository, setSelectedRepository] = useState<Repository | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<string | undefined>(undefined);
  // Track selected branch for each repo (key: "owner/repo", value: branchName)
  const [selectedBranches, setSelectedBranches] = useState<Record<string, string>>({});
  // Track processing state per branch (key: "owner/repo/branch", value: "freezing" | "unfreezing" | null)
  const [processingBranches, setProcessingBranches] = useState<
    Record<string, 'freezing' | 'unfreezing' | null>
  >({});
  const [reposExpanded, setReposExpanded] = useState(false);

  // Ref for infinite scroll observer
  const loadMoreRef = React.useRef<HTMLDivElement>(null);

  const stats = {
    total: repositories.length,
    frozen: repositories.filter(r => r.frozen).length,
    active: repositories.length - repositories.filter(r => r.frozen).length,
  };

  // SSE message handler
  const handleSSEMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        // Handle different SSE message formats
        if (data.type === 'connected') {
          console.log('SSE connected:', data);
          return;
        }

        // GitHub webhook events have format: { type: 'github_webhook', event: 'pull_request', action: 'opened' }
        if (data.type === 'github_webhook') {
          if (
            data.event === 'pull_request' ||
            data.event === 'installation_repositories' ||
            data.event === 'push'
          ) {
            console.log(`GitHub ${data.event} event received:`, data);
            loadRepositories();
            loadActivityHistory();
          }
          return;
        }

        // Legacy event format for backwards compatibility
        switch (data.type) {
          case messages.events.repositoryFrozen:
          case messages.events.repositoryUnfrozen:
          case 'schedule_created':
          case 'schedule_updated':
          case 'schedule_deleted':
            loadRepositories();
            loadActivityHistory();
            break;
        }
      } catch (error) {
        console.error('Error parsing SSE data:', error);
      }
    },
    [loadRepositories, loadActivityHistory]
  );

  // Handle SSE events - uses singleton connection
  useSSE(handleSSEMessage);

  // Set installation ID in store when it changes
  useEffect(() => {
    setCurrentInstallation(authStatus.currentInstallation);
  }, [authStatus.currentInstallation, setCurrentInstallation]);

  // Load data only on first mount if authenticated
  useEffect(() => {
    if (authStatus.authenticated && authStatus.currentInstallation && initialLoad) {
      loadRepositories();
      loadActivityHistory();
    }
  }, [authStatus.authenticated, authStatus.currentInstallation]);

  // Infinite scroll observer for activity log
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMoreActivity && !activityLoading) {
          loadMoreActivity();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMoreActivity, activityLoading, loadMoreActivity]);

  async function handleFreeze(repo: Repository) {
    if (!repo.isAccessible) {
      const org = authStatus.currentOrganization;
      if (org && org.subscriptionTier === 'FREE') {
        const confirmUpgrade = confirm(
          `You've reached the free tier limit of ${organizationInfo?.repoLimit || 3} repositories.\n\n` +
            'Would you like to upgrade to Pro for unlimited repositories?'
        );
        if (confirmUpgrade) {
          window.location.hash = '#settings';
        }
        return;
      }
    }

    const selectedBranch = getSelectedBranch(repo);
    const branchKey = `${repo.owner}/${repo.repo}/${selectedBranch}`;

    // Set freezing state
    setProcessingBranches(prev => ({ ...prev, [branchKey]: 'freezing' }));

    try {
      const result = await freezeBranch(repo.owner, repo.repo, selectedBranch);

      if (result.success) {
        showToast(`Branch ${selectedBranch} frozen successfully`, 'success');
        loadActivityHistory();
      } else {
        if (result.requiresUpgrade) {
          const confirmUpgrade = confirm(result.message + '\n\nWould you like to upgrade now?');
          if (confirmUpgrade) {
            window.location.hash = '#settings';
          }
        } else {
          showToast(`Failed to freeze branch: ${result.error || messages.errors.general}`, 'error');
        }
      }
    } finally {
      // Clear freezing state
      setProcessingBranches(prev => ({ ...prev, [branchKey]: null }));
    }
  }

  async function handleUnfreeze(repo: Repository) {
    if (!repo.isAccessible) {
      const org = authStatus.currentOrganization;
      if (org && org.subscriptionTier === 'FREE') {
        const confirmUpgrade = confirm(
          `You've reached the free tier limit of ${organizationInfo?.repoLimit || 3} repositories.\n\n` +
            'Would you like to upgrade to Pro for unlimited repositories?'
        );
        if (confirmUpgrade) {
          window.location.hash = '#settings';
        }
        return;
      }
    }

    const selectedBranch = getSelectedBranch(repo);
    const branchKey = `${repo.owner}/${repo.repo}/${selectedBranch}`;

    // Set unfreezing state
    setProcessingBranches(prev => ({ ...prev, [branchKey]: 'unfreezing' }));

    try {
      const result = await unfreezeBranch(repo.owner, repo.repo, selectedBranch);

      if (result.success) {
        showToast(`Branch ${selectedBranch} unfrozen successfully`, 'success');
        loadActivityHistory();
      } else {
        showToast(`Failed to unfreeze branch: ${result.error || messages.errors.general}`, 'error');
      }
    } finally {
      // Clear unfreezing state
      setProcessingBranches(prev => ({ ...prev, [branchKey]: null }));
    }
  }

  function toggleRepoSelection(repoKey: string) {
    setSelectedRepos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(repoKey)) {
        newSet.delete(repoKey);
      } else {
        newSet.add(repoKey);
      }
      return newSet;
    });
  }

  function toggleSelectAll() {
    if (selectedRepos.size === accessibleRepos.length) {
      setSelectedRepos(new Set());
    } else {
      setSelectedRepos(new Set(accessibleRepos.map(r => `${r.owner}/${r.repo}`)));
    }
  }

  function clearSelection() {
    setSelectedRepos(new Set());
  }

  async function handleBulkFreeze() {
    const reposToFreeze = repositories.filter(
      r => selectedRepos.has(`${r.owner}/${r.repo}`) && !r.frozen
    );

    if (reposToFreeze.length === 0) {
      showToast('All selected repositories are already frozen', 'info');
      return;
    }

    setBulkOperationInProgress(true);

    const result = await bulkFreezeRepositories(reposToFreeze);

    if (result.success) {
      showToast(`Successfully froze ${result.summary?.succeeded || 0} repositories`, 'success');
      loadActivityHistory();
      clearSelection();
    } else {
      const succeeded = result.summary?.succeeded || 0;
      const failed = result.summary?.failed || 0;
      if (succeeded > 0) {
        showToast(`Froze ${succeeded} repositories, ${failed} failed`, 'warning');
      } else {
        showToast(
          `Failed to freeze repositories: ${result.error || messages.errors.general}`,
          'error'
        );
      }
    }

    setBulkOperationInProgress(false);
  }

  async function handleBulkUnfreeze() {
    const reposToUnfreeze = repositories.filter(
      r => selectedRepos.has(`${r.owner}/${r.repo}`) && r.frozen
    );

    if (reposToUnfreeze.length === 0) {
      showToast('All selected repositories are already unfrozen', 'info');
      return;
    }

    setBulkOperationInProgress(true);

    const result = await bulkUnfreezeRepositories(reposToUnfreeze);

    if (result.success) {
      showToast(`Successfully unfroze ${result.summary?.succeeded || 0} repositories`, 'success');
      loadActivityHistory();
      clearSelection();
    } else {
      const succeeded = result.summary?.succeeded || 0;
      const failed = result.summary?.failed || 0;
      if (succeeded > 0) {
        showToast(`Unfroze ${succeeded} repositories, ${failed} failed`, 'warning');
      } else {
        showToast(
          `Failed to unfreeze repositories: ${result.error || messages.errors.general}`,
          'error'
        );
      }
    }

    setBulkOperationInProgress(false);
  }

  function handleOpenExemptionModal(repo: Repository, branchName?: string) {
    setSelectedRepository(repo);
    setSelectedBranch(branchName);
    setExemptionModalOpen(true);
  }

  function handleBranchChange(repo: Repository, branchName: string) {
    const repoKey = `${repo.owner}/${repo.repo}`;
    setSelectedBranches(prev => ({
      ...prev,
      [repoKey]: branchName,
    }));
  }

  function getSelectedBranch(repo: Repository): string {
    const repoKey = `${repo.owner}/${repo.repo}`;
    return selectedBranches[repoKey] || repo.branch || 'main';
  }

  function getBranchData(repo: Repository, branchName: string) {
    return repo.frozenBranches?.find(b => b.branchName === branchName);
  }

  async function handleSaveExemptions(exemptPRs: string[], branch?: string) {
    if (!selectedRepository) return;

    const result = await updateExemptions(
      selectedRepository.owner,
      selectedRepository.repo,
      exemptPRs,
      branch
    );

    if (result.success) {
      showToast('Exemptions updated successfully', 'success');
      // Clear branch selection when modal closes
      setSelectedBranch(undefined);
    } else {
      throw new Error(result.error || 'Failed to update exemptions');
    }
  }

  function handleCloseExemptionModal() {
    setExemptionModalOpen(false);
    setSelectedBranch(undefined);
  }

  // Search and filter logic
  const filteredRepos = repositories.filter(repo => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const fullName = `${repo.owner}/${repo.repo}`.toLowerCase();
    const owner = repo.owner.toLowerCase();
    const repoName = repo.repo.toLowerCase();
    return fullName.includes(query) || owner.includes(query) || repoName.includes(query);
  });

  // Sorting logic
  const sortedRepos = [...filteredRepos].sort((a, b) => {
    if (sortBy === 'recent') {
      // Sort by most recent action first
      const aTime = a.lastAction ? new Date(a.lastAction).getTime() : 0;
      const bTime = b.lastAction ? new Date(b.lastAction).getTime() : 0;

      if (aTime !== bTime) {
        return bTime - aTime; // Most recent first
      }
      // If same time or both null, fall back to alphabetical
      return `${a.owner}/${a.repo}`.localeCompare(`${b.owner}/${b.repo}`);
    } else {
      // Alphabetical sort
      return `${a.owner}/${a.repo}`.localeCompare(`${b.owner}/${b.repo}`);
    }
  });

  // Filter repos to only accessible ones for selection
  const accessibleRepos = sortedRepos.filter(r => r.isAccessible);
  const selectedReposList = sortedRepos.filter(r => selectedRepos.has(`${r.owner}/${r.repo}`));
  const allSelectedFrozen = selectedReposList.length > 0 && selectedReposList.every(r => r.frozen);
  const allSelectedUnfrozen =
    selectedReposList.length > 0 && selectedReposList.every(r => !r.frozen);
  const hasSelection = selectedRepos.size > 0;

  return (
    <div className="page">
      <PageHeader
        title="Repositories"
        subtitle="Monitor and manage frozen repositories"
        stats={
          <>
            <PageHeader.StatCard value={stats.total} label="Total" />
            <PageHeader.StatCard value={stats.frozen} label="Frozen" variant="frozen" />
            <PageHeader.StatCard value={stats.active} label="Active" variant="active" />
          </>
        }
        toolbar={
          !initialLoad && repositories.length > 0 ? (
            <div className="repos-toolbar">
              <SearchInput
                placeholder="Search repositories..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onClear={() => setSearchQuery('')}
              />
              <div className="sort-container">
                <i className="fa-solid fa-arrow-down-short-wide sort-icon"></i>
                <select
                  className="sort-select"
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as 'recent' | 'alphabetical')}
                >
                  <option value="recent">Recently Updated</option>
                  <option value="alphabetical">Alphabetical</option>
                </select>
              </div>
            </div>
          ) : undefined
        }
      />

      <div className="page-content">
        {/* Repository Grid */}
        <div className="repos-section">
          {/* Loading State */}
          {initialLoad && loading && <LoadingSpinner message="Loading repositories..." />}

          {/* GitHub Pro Notice for Private Repos */}
          {!initialLoad && repositories.length > 0 && showGitHubProNotice && (
            <div
              className="limit-notice"
              style={{
                background: '#e7f3ff',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              <i
                className="fa-solid fa-info-circle"
                style={{ color: '#0969da', fontSize: '16px', flexShrink: 0, marginRight: '12px' }}
              ></i>
              <span style={{ color: '#0969da', flex: 1 }}>
                <strong>Note:</strong> Private repositories require the repository owner to have a
                GitHub Pro, Team, or Enterprise plan to enable branch protection rules.{' '}
                <a
                  href="https://docs.github.com/en/get-started/learning-about-github/githubs-plans"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#0969da', fontWeight: 600, textDecoration: 'underline' }}
                >
                  Learn more
                </a>
              </span>
              <button
                onClick={() => {
                  setShowGitHubProNotice(false);
                  localStorage.setItem('hideGitHubProNotice', 'true');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0969da',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  marginLeft: '12px',
                  fontSize: '18px',
                  lineHeight: '1',
                  flexShrink: 0,
                }}
                title="Dismiss"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          )}

          {/* Limit Notice for Free Tier */}
          {!initialLoad &&
            organizationInfo &&
            organizationInfo.configuredRepos > organizationInfo.repoLimit &&
            authStatus.currentOrganization?.subscriptionTier === 'FREE' && (
              <div
                className="limit-notice"
                style={{
                  background: '#fff3cd',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <i
                  className="fa-solid fa-info-circle"
                  style={{ color: '#856404', fontSize: '16px', flexShrink: 0, marginRight: '12px' }}
                ></i>
                <span style={{ color: '#856404' }}>
                  Only {organizationInfo?.repoLimit || 0} of{' '}
                  {organizationInfo?.configuredRepos || 0} configured repositories can be managed.{' '}
                  {organizationInfo.hiddenRepos > 0 && (
                    <>
                      You have {organizationInfo.hiddenRepos} more configured{' '}
                      {organizationInfo.hiddenRepos === 1 ? 'repository' : 'repositories'} that{' '}
                      {organizationInfo.hiddenRepos === 1 ? 'is' : 'are'} hidden.{' '}
                    </>
                  )}
                  <a
                    href="#settings"
                    style={{ color: '#522D6D', fontWeight: 600, textDecoration: 'underline' }}
                  >
                    Upgrade to Pro
                  </a>{' '}
                  to manage all repositories.
                </span>
              </div>
            )}

          {/* Repository Cards */}
          {!initialLoad && sortedRepos.length > 0 && (
            <>
              <div
                className={`repos-grid ${!reposExpanded && sortedRepos.length > 8 ? 'repos-grid-collapsed' : ''}`}
              >
                {(reposExpanded ? sortedRepos : sortedRepos.slice(0, 8)).map(repo => {
                  const repoKey = `${repo.owner}/${repo.repo}`;
                  const isSelected = selectedRepos.has(repoKey);
                  return (
                    <div key={repoKey} className={`repo-card ${isSelected ? 'selected' : ''}`}>
                      <div className="repo-card-header">
                        <div className="repo-card-header-top">
                          {repo.isAccessible && (
                            <label
                              className="repo-checkbox-label"
                              onClick={e => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                className="repo-checkbox"
                                checked={isSelected}
                                onChange={() => toggleRepoSelection(repoKey)}
                              />
                            </label>
                          )}
                          <div className="repo-info">
                            <a
                              href={`https://github.com/${repo.owner}/${repo.repo}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="repo-link"
                            >
                              <div className="repo-owner">{repo.owner}/</div>
                              <h4 className="repo-name">{repo.repo}</h4>
                            </a>
                          </div>
                          <button
                            onClick={() => handleOpenExemptionModal(repo, getSelectedBranch(repo))}
                            className="repo-settings-btn"
                            title="Configure freeze exceptions"
                          >
                            <i className="fa-solid fa-gear"></i>
                          </button>
                        </div>
                        <div className="repo-card-header-bottom">
                          <BranchSelector
                            owner={repo.owner}
                            repo={repo.repo}
                            selectedBranch={getSelectedBranch(repo)}
                            onBranchChange={branchName => handleBranchChange(repo, branchName)}
                            onBranchesLoaded={() => loadRepositories()}
                          />
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {(() => {
                              const currentBranch = getBranchData(repo, getSelectedBranch(repo));
                              const isFrozen = currentBranch?.frozen || false;
                              return (
                                <Badge variant={isFrozen ? 'frozen' : 'active'}>
                                  {isFrozen ? 'Frozen' : 'Active'}
                                </Badge>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                      <div className="repo-card-body">
                        <div className="repo-stats">
                          <div
                            className="repo-stat"
                            title={`Open PRs targeting ${getSelectedBranch(repo)}`}
                          >
                            <div className="stat-label">Open PRs</div>
                            <div className="stat-value-container">
                              <i className="stat-icon fa-solid fa-code-pull-request"></i>
                              <span className="stat-text pr-count">
                                {(() => {
                                  const currentBranch = getBranchData(
                                    repo,
                                    getSelectedBranch(repo)
                                  );
                                  const count = currentBranch?.prCount ?? 0;
                                  return count;
                                })()}
                              </span>
                            </div>
                          </div>
                          <div
                            className="repo-stat"
                            title={(() => {
                              const currentBranch = getBranchData(repo, getSelectedBranch(repo));
                              const lastAction = getBranchLastAction(currentBranch);
                              return `Last frozen/unfrozen: ${formatLastAction(lastAction)}`;
                            })()}
                          >
                            <div className="stat-label">Last Action</div>
                            <div className="stat-value-container">
                              <i className="stat-icon fa-solid fa-snowflake"></i>
                              <span className="stat-text last-action">
                                {(() => {
                                  const currentBranch = getBranchData(
                                    repo,
                                    getSelectedBranch(repo)
                                  );
                                  const lastAction = getBranchLastAction(currentBranch);
                                  return formatLastAction(lastAction);
                                })()}
                              </span>
                            </div>
                          </div>
                          <div
                            className="repo-stat clickable"
                            onClick={() => navigate(`/schedules?repo=${repoKey}`)}
                            style={{ cursor: 'pointer' }}
                            title="View schedules for this repository"
                          >
                            <div className="stat-label">Schedules</div>
                            <div className="stat-value-container">
                              <i className="stat-icon fa-solid fa-calendar-check"></i>
                              <span className="stat-text schedule-count">
                                {repo.scheduleCount || 0}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="repo-card-footer">
                        {(() => {
                          const selectedBranchName = getSelectedBranch(repo);
                          const branchKey = `${repo.owner}/${repo.repo}/${selectedBranchName}`;
                          const processingState = processingBranches[branchKey];
                          const currentBranch = getBranchData(repo, selectedBranchName);
                          const isFrozen = currentBranch?.frozen || false;
                          const hasActiveSchedule = currentBranch?.hasActiveSchedule || false;
                          const isProcessing =
                            processingState === 'freezing' || processingState === 'unfreezing';
                          const isDisabled =
                            isProcessing || !repo.isAccessible || hasActiveSchedule;

                          return isFrozen ? (
                            <Button
                              onClick={() => handleUnfreeze(repo)}
                              variant="unfreeze"
                              disabled={isDisabled}
                              icon={`fa-solid ${processingState === 'unfreezing' ? 'fa-spinner fa-spin' : hasActiveSchedule ? 'fa-calendar-check' : !repo.isAccessible ? 'fa-lock' : 'fa-fire-flame-curved'}`}
                              className={!repo.isAccessible ? 'disabled' : ''}
                              title={
                                hasActiveSchedule
                                  ? 'Branch is controlled by an active schedule'
                                  : undefined
                              }
                            >
                              {processingState === 'unfreezing'
                                ? 'Unfreezing...'
                                : hasActiveSchedule
                                  ? 'Scheduled'
                                  : !repo.isAccessible
                                    ? 'Upgrade'
                                    : 'Unfreeze'}
                            </Button>
                          ) : (
                            <Button
                              onClick={() => handleFreeze(repo)}
                              variant="freeze"
                              disabled={isDisabled}
                              icon={`fa-solid ${processingState === 'freezing' ? 'fa-spinner fa-spin' : hasActiveSchedule ? 'fa-calendar-check' : !repo.isAccessible ? 'fa-lock' : 'fa-snowflake'}`}
                              className={!repo.isAccessible ? 'disabled' : ''}
                              title={
                                hasActiveSchedule
                                  ? 'Branch is controlled by an active schedule'
                                  : undefined
                              }
                            >
                              {processingState === 'freezing'
                                ? 'Freezing...'
                                : hasActiveSchedule
                                  ? 'Scheduled'
                                  : !repo.isAccessible
                                    ? 'Upgrade'
                                    : 'Freeze'}
                            </Button>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
              {sortedRepos.length > 8 && (
                <div className="repos-expand-toggle">
                  <button
                    onClick={() => setReposExpanded(!reposExpanded)}
                    className="btn-expand-repos"
                  >
                    {reposExpanded ? (
                      <>
                        <span>Show less</span>
                        <i className="fa-solid fa-chevron-up"></i>
                      </>
                    ) : (
                      <>
                        <span>Show all {sortedRepos.length} repositories</span>
                        <i className="fa-solid fa-chevron-down"></i>
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}

          {/* No Search Results */}
          {!initialLoad && repositories.length > 0 && sortedRepos.length === 0 && searchQuery && (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="fa-solid fa-magnifying-glass"></i>
              </div>
              <h3>No repositories found</h3>
              <p>No repositories match "{searchQuery}"</p>
              <button onClick={() => setSearchQuery('')} className="btn btn-secondary">
                Clear Search
              </button>
            </div>
          )}

          {/* Empty State */}
          {!initialLoad && repositories.length === 0 && authStatus.currentInstallation && (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="fa-solid fa-box-open"></i>
              </div>
              <h3>No repositories found</h3>
              <p>Install the GitHub App on repositories to manage them</p>
              <div className="empty-state-actions">
                <a
                  href={`https://github.com/apps/${import.meta.env.VITE_PUBLIC_GITHUB_APP_SLUG || 'yukon-freeze-manager'}/installations/new`}
                  className="btn btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa-brands fa-github"></i>
                  <span>Configure GitHub App</span>
                </a>
              </div>
            </div>
          )}

          {/* No Installation State */}
          {!authStatus.currentInstallation && (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="fa-solid fa-download"></i>
              </div>
              <h3>GitHub App Not Installed</h3>
              <p>Install the GitHub App on repositories to manage them</p>
              <div className="empty-state-actions">
                <a
                  href={`https://github.com/apps/${import.meta.env.VITE_PUBLIC_GITHUB_APP_SLUG || 'yukon-freeze-manager'}/installations/new`}
                  className="btn btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa-brands fa-github"></i>
                  <span>Configure GitHub App</span>
                </a>
                <button onClick={loadRepositories} className="btn btn-secondary">
                  <i className="fa-solid fa-arrow-rotate-right"></i>
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bulk Action Bar */}
        {hasSelection && (
          <div className="bulk-action-bar">
            <div className="bulk-action-info">
              <i className="fa-solid fa-check-circle"></i>
              <span>
                {selectedRepos.size} {selectedRepos.size === 1 ? 'repo' : 'repos'} selected
              </span>
            </div>
            <div className="bulk-action-buttons">
              <button
                onClick={handleBulkFreeze}
                className="btn btn-freeze"
                disabled={bulkOperationInProgress || allSelectedFrozen}
              >
                <i
                  className={`fa-solid ${bulkOperationInProgress ? 'fa-spinner fa-spin' : 'fa-snowflake'}`}
                ></i>
                <span>Freeze</span>
              </button>
              <button
                onClick={handleBulkUnfreeze}
                className="btn btn-unfreeze"
                disabled={bulkOperationInProgress || allSelectedUnfrozen}
              >
                <i
                  className={`fa-solid ${bulkOperationInProgress ? 'fa-spinner fa-spin' : 'fa-fire-flame-curved'}`}
                ></i>
                <span>Unfreeze</span>
              </button>
              <button
                onClick={clearSelection}
                className="btn btn-clear-selection"
                disabled={bulkOperationInProgress}
                title="Clear selection"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>
        )}

        {/* Activity Feed */}
        <div className="activity-section">
          <h3>Recent Activity</h3>
          <div className="activity-log">
            {activityLog.map((activity, index) => (
              <div key={activity.id || index} className={`activity-item activity-${activity.type}`}>
                <i
                  className={`activity-icon fa-solid ${activity.type === 'freeze' ? 'fa-snowflake' : 'fa-fire-flame-curved'}`}
                ></i>
                <span className="activity-message">{activity.message}</span>
                <span className="activity-time">{formatActivityTime(activity.timestamp)}</span>
              </div>
            ))}
            {activityLog.length === 0 && (
              <div className="activity-empty">
                <i className="fa-regular fa-clock activity-empty-icon"></i>
                <span>No recent activity</span>
                <p className="activity-empty-subtitle">
                  Freeze and unfreeze actions will appear here
                </p>
              </div>
            )}
            {/* Infinite scroll trigger */}
            {activityLog.length > 0 && hasMoreActivity && (
              <div ref={loadMoreRef} style={{ padding: '20px', textAlign: 'center' }}>
                {activityLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    <span style={{ color: 'var(--text-secondary)' }}>Loading more...</span>
                  </div>
                ) : (
                  <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                    Scroll for more
                  </span>
                )}
              </div>
            )}
            {activityLog.length > 0 && !hasMoreActivity && (
              <div
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                }}
              >
                No more activity to load
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Exemption Modal */}
      <ExemptionModal
        isOpen={exemptionModalOpen}
        onClose={handleCloseExemptionModal}
        onSave={handleSaveExemptions}
        repository={selectedRepository}
        branch={selectedBranch}
      />
    </div>
  );
}
